import { z } from 'zod';
import {
  DEFAULT_THEME,
  themeOverridesSchema,
  themeSchema,
  type Format,
  type NewsletterSignup,
  type Presentation,
  type Theme,
  type ThemeOverrides,
} from './content';
import { FORMAT_RULES } from './formats';
import {
  CONSENT_STORED_MAX,
  DEFAULT_CONSENT_TEXT,
  DEFAULT_NEWSLETTER_CONSENT_TEXT,
  parseConsentText,
  type ConsentSegment,
} from './consent';

/**
 * Brand level configuration, and the one place inheritance is worked out.
 *
 * A publisher is an org with several mastheads under it. The org carries defaults, a brand may
 * override any of them, and anything neither has answered falls to the system default in this
 * package. Three layers, resolved per field, in `resolveBrandConfig` and nowhere else: a second
 * implementation of "which colour wins" is a second implementation that will disagree with the
 * first, and the disagreement would show up as a widget on a live article looking wrong.
 */

/**
 * A domain a brand's embeds are served from.
 *
 * Labels only: no scheme, no port, no path. `originAllowed` in the API matches a host against these
 * as a suffix, so `herald.co.uk` already covers `www.herald.co.uk` and every other subdomain, and
 * one entry is usually the whole answer. It stays a list because a publisher wants their staging
 * domain alongside their live one while they are trying it.
 */
export const DOMAIN_MAX_LENGTH = 253;

const DOMAIN_PATTERN =
  /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)*$/;

export const domainSchema = z
  .string()
  .min(1)
  .max(DOMAIN_MAX_LENGTH)
  .regex(DOMAIN_PATTERN, 'expected a domain like herald.co.uk');

/** At least one, because a brand with none can publish nothing. See the publish route. */
export const MAX_DOMAINS = 50;

export const domainsSchema = z.array(domainSchema).min(1).max(MAX_DOMAINS);

/**
 * Turn what an editor pasted into a domain, or null if it is not one.
 *
 * Editors paste `https://www.herald.co.uk/news/`, or type a leading dot, or a wildcard, because
 * that is what other products ask for. Rejecting those would be technically correct and useless.
 * Anything left over that is not a plain hostname is refused rather than guessed at.
 */
/**
 * Suffixes anybody can register under, which therefore cannot be an allowlist entry.
 *
 * The origin check matches a host or any subdomain of it, so a brand allowing `co.uk` would accept
 * events from every website in the country. This is not the public suffix list and does not try to
 * be: pulling in a dependency that changes weekly, to catch a typo, is the wrong trade. It is the
 * suffixes a British or European publisher might plausibly paste by mistake, plus the bare tlds.
 * Anything it misses still has to survive the rest of normalisation.
 */
const PUBLIC_SUFFIXES = new Set([
  'co.uk',
  'org.uk',
  'ac.uk',
  'gov.uk',
  'net.uk',
  'me.uk',
  'ltd.uk',
  'plc.uk',
  'sch.uk',
  'com.au',
  'net.au',
  'org.au',
  'co.nz',
  'co.za',
  'com.br',
  'co.jp',
  'co.in',
  'com.tr',
  'co.ie',
  'com.es',
  'com.pl',
  'com.pt',
  'com.mx',
  'com.ar',
]);

export function normaliseDomain(input: string): string | null {
  let value = input.trim().toLowerCase();
  if (value === '') return null;

  value = value.replace(/^[a-z][a-z0-9+.-]*:\/\//, '');
  // A pasted url can carry credentials, a path, a query or a fragment. Take the authority only.
  value = value.split('/')[0] ?? '';
  value = value.split('?')[0] ?? '';
  value = value.split('#')[0] ?? '';
  value = value.split('@').pop() ?? '';
  // A port is not part of the host the origin check compares against.
  value = value.split(':')[0] ?? '';
  // `*.herald.co.uk` and `.herald.co.uk` both mean "and its subdomains", which the suffix match
  // already does, so the prefix is noise rather than an instruction.
  value = value.replace(/^\*\./, '').replace(/^\./, '').replace(/\.$/, '');

  if (value === '') return null;

  /**
   * A bare label or a public suffix would hand the brand every site beneath it, so `uk` and
   * `co.uk` are both refused. `localhost` is the exception: it has no subdomains anybody else can
   * register, and a publisher testing an embed on their own machine needs it.
   */
  if (value !== 'localhost' && !value.includes('.')) return null;
  if (PUBLIC_SUFFIXES.has(value)) return null;

  return domainSchema.safeParse(value).success ? value : null;
}

/**
 * Normalise a list, drop anything unusable, and remove duplicates while keeping the order typed.
 *
 * Returns the domains it could read and the raw entries it could not, so a route can refuse with
 * the offending entry rather than with "invalid request".
 */
export function normaliseDomains(input: readonly string[]): {
  domains: string[];
  rejected: string[];
} {
  const domains: string[] = [];
  const rejected: string[] = [];

  for (const entry of input) {
    const domain = normaliseDomain(entry);
    if (domain === null) {
      rejected.push(entry);
      continue;
    }
    if (!domains.includes(domain)) domains.push(domain);
  }

  return { domains, rejected };
}

/**
 * Single or double opt in, which is the whole of what `POST /v1/subscriptions` answers with.
 *
 * Single means the address joins the list on the spot. Double means it waits until the reader
 * proves they can read the inbox, which is what `pending` means to the widget and to the reader.
 *
 * It lives here rather than in `packages/db` because the dashboard has to render the same three
 * layers this file resolves, and a package the dashboard cannot import means the default gets
 * written down a second time in the editor and drifts from the one the API applies.
 */
export const newsletterOptInSchema = z.enum(['single', 'double']);
export type NewsletterOptIn = z.infer<typeof newsletterOptInSchema>;

/** Nobody has chosen, at either layer, so double: the mode that proves the address. */
export const DEFAULT_NEWSLETTER_OPT_IN: NewsletterOptIn = 'double';

/**
 * What an org or a brand stores: overrides, not values.
 *
 * Every field is optional at both levels, and absent means "inherit". An empty string for
 * `consent_text` is a value rather than an absence: it is how a brand says "show no notice here"
 * over an org default that shows one.
 */
export const brandSettingsSchema = z
  .object({
    theme: themeOverridesSchema.optional(),
    consent_text: z.string().max(CONSENT_STORED_MAX).optional(),
    /**
     * The newsletter wording, which is a different statement from the one above.
     *
     * `consent_text` explains what happens to an answer. This is the marketing consent a reader
     * gives by ticking a box before handing over an address, so it is written once per masthead and
     * inherited by every interaction that offers the signup. An empty string is a value here too,
     * and it means the same thing it means above: show nothing. With nothing to show there is
     * nothing that could honestly be recorded, so the step does not appear at all.
     */
    newsletter_consent_text: z.string().max(CONSENT_STORED_MAX).optional(),
    /**
     * How this masthead's newsletter list takes a signup.
     *
     * An override at both layers, like everything else here. A group that runs thirty mastheads on
     * single opt in sets it once on the org, and a title that wants the confirmation step sets it
     * on itself; absent at both means the system default.
     */
    newsletter_opt_in: newsletterOptInSchema.optional(),
  })
  .strict();

export type BrandSettings = z.infer<typeof brandSettingsSchema>;

export interface ResolvedBrandConfig {
  /** Every field filled in, ready to become a presentation. */
  readonly theme: Theme;
  /** The sanitised markup, which is what staleness is compared against. */
  readonly consent_text: string;
  /** The same text as data, which is what a published document and the widget carry. */
  readonly consent_segments: ConsentSegment[];
  /** The newsletter wording, resolved the same way and stale checked the same way. */
  readonly newsletter_consent_text: string;
  /** And as data, which is what the published `signup` block carries. */
  readonly newsletter_consent_segments: ConsentSegment[];
  /** Single or double opt in, resolved through the same three layers as the wording above. */
  readonly newsletter_opt_in: NewsletterOptIn;
}

/**
 * Brand value, then org default, then the system default.
 *
 * Per field rather than per object: a brand that sets only `accent_colour` keeps the org's font and
 * the system's corner radius. Doing it per object would mean a brand touching one colour silently
 * dropped everything else the org had set, which is the kind of thing nobody notices until a
 * masthead goes out in the wrong typeface.
 */
export function resolveBrandConfig(
  org: BrandSettings | undefined,
  brand: BrandSettings | undefined,
): ResolvedBrandConfig {
  const theme = themeSchema.parse({
    ...DEFAULT_THEME,
    ...defined(org?.theme),
    ...defined(brand?.theme),
  });

  const consentText = brand?.consent_text ?? org?.consent_text ?? DEFAULT_CONSENT_TEXT;

  /**
   * Resolved per field beside the notice above rather than with it, which is the same rule again.
   *
   * A brand that writes its own newsletter wording keeps the org's answer notice, and one that
   * writes its own answer notice keeps the org's newsletter wording. Resolving the two together as
   * one "consent" object would mean touching either silently dropped the other, and the way a
   * publisher would find out is a reader being asked to agree to a sentence nobody chose.
   */
  const newsletterConsentText =
    brand?.newsletter_consent_text ??
    org?.newsletter_consent_text ??
    DEFAULT_NEWSLETTER_CONSENT_TEXT;

  /**
   * Per field again, and beside the wording rather than folded into it.
   *
   * The wording and the mode are two separate decisions about the same list: a title that writes
   * its own newsletter sentence keeps the group's opt in mode, and one that wants the confirmation
   * step keeps the group's sentence. Resolving them together would mean setting either silently
   * dropped the other, and a publisher would find out when a reader was asked to agree to a
   * sentence nobody chose, or joined a list without the confirmation the group insists on.
   *
   * The org layer is the reason this exists at all: without it a group of thirty mastheads sets
   * single opt in thirty times, once per brand, with no way to say it once for the publisher.
   */
  const newsletterOptIn =
    brand?.newsletter_opt_in ?? org?.newsletter_opt_in ?? DEFAULT_NEWSLETTER_OPT_IN;

  return {
    theme,
    consent_text: consentText,
    consent_segments: parseConsentText(consentText),
    newsletter_consent_text: newsletterConsentText,
    newsletter_consent_segments: parseConsentText(newsletterConsentText),
    newsletter_opt_in: newsletterOptIn,
  };
}

/**
 * The presentation a version is published with.
 *
 * The interaction wins if it has one, otherwise the brand's theme plus the `show_results` the
 * format allows. This runs at publish time and the answer is frozen into the version, which is what
 * keeps a published document self contained: the widget reads one static file with no server behind
 * it, so nothing can be resolved at render time. It also means editing a brand's theme leaves
 * everything already published exactly as it was, which is correct for the consent notice, since
 * that is a record of what a reader was actually told.
 */
export function resolvePresentation(
  format: Format,
  theme: Theme,
  override: Presentation | undefined,
): Presentation {
  if (override !== undefined) return override;

  return {
    ...theme,
    show_results: FORMAT_RULES[format].hasResults ? 'after_answer' : 'never',
  };
}

/**
 * The signup a version is published with, with the wording it will be published with filled in.
 *
 * The same shape of decision as `resolvePresentation` and frozen at the same moment, for the same
 * reason and one more. The reason: the widget reads one static file with no server behind it, so
 * anything not resolved by now can never be resolved. The one more: the published wording is the
 * record of what a reader was actually told before they handed over an address, so editing a
 * brand's newsletter text must leave every version already published saying exactly what it said.
 *
 * The interaction wins if it wrote its own wording. Note that an interaction setting an empty
 * string is setting a value, not asking to inherit, and means the same as it does on a brand.
 */
export function resolveSignup(
  signup: NewsletterSignup | undefined,
  newsletterConsentText: string,
): NewsletterSignup | undefined {
  if (signup === undefined) return undefined;
  return { ...signup, consent_text: signup.consent_text ?? newsletterConsentText };
}

/**
 * Drop keys explicitly set to undefined.
 *
 * A partial coming back from DynamoDB or through a JSON body can carry `{ accent_colour: undefined
 * }`, and spreading that over a lower layer would erase the lower layer's value with nothing.
 */
function defined(overrides: ThemeOverrides | undefined): ThemeOverrides {
  if (!overrides) return {};
  const kept: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(overrides)) {
    if (value !== undefined) kept[key] = value;
  }
  return kept as ThemeOverrides;
}
